import React, { useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import PageHeader from "../../../components/layout/PageHeader";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import PickerChips from "../../../components/ui/PickerChips";
import { useCompanies } from "../../../api/queries/organizationQueries";
import {
  useAnnouncements,
  useDeleteAnnouncement,
  useSaveAnnouncement,
} from "../../../api/queries/widgetQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import useBranchTargets, {
  BRANCH_SCOPE_HINT,
} from "../../../hooks/useBranchTargets";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";
import {
  ALL_COMPANIES,
  BODY_MAX,
  STATUS,
  TITLE_MAX,
  audienceText,
  emptyForm,
  formProblems,
  fromRow,
  toPayload,
} from "./announcementFormat";
import "../admin.css";

const EMPTY = [];
const TABS = [
  { key: "active", label: "Active" },
  { key: "scheduled", label: "Scheduled" },
  { key: "expired", label: "Expired" },
  { key: "inactive", label: "Inactive" },
  { key: "all", label: "All" },
];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};

const AnnouncementDialog = ({ row, onClose }) => {
  const user = useCurrentUser();
  const { superAdmin } = usePermissions();
  const { data: companies = EMPTY } = useCompanies();
  const [form, setForm] = useState(() => (row ? fromRow(row) : emptyForm()));
  const [errors, setErrors] = useState([]);
  const save = useSaveAnnouncement();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const branchCompany =
    form.companyId && form.companyId !== ALL_COMPANIES
      ? form.companyId
      : user.companyId;
  // BRANCH scope: only the branches they manage (and the company option is hidden)
  const { branchOnly, options } = useBranchTargets(
    "announcements",
    branchCompany,
  );

  const submit = () => {
    const problems = formProblems(form);
    if (branchOnly && !form.branchIds.length)
      problems.push("Pick the branches it is for.");
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { id: row?.id, body: toPayload(form, { isEdit: !!row }) },
      {
        onSuccess: () => {
          toast.success(row ? "Announcement saved." : "Announcement posted.");
          onClose();
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the announcement.")),
      },
    );
  };

  return (
    <Dialog
      title={row ? "Edit announcement" : "New announcement"}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {row ? "Save" : form.publishAt ? "Schedule" : "Post now"}
          </button>
        </>
      }
    >
      {errors.length > 0 && (
        <div className="alert alert-danger fs-085 py-2">
          <ul className="mb-0 ps-3">
            {errors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row g-3">
        <div className="col-12">
          <label htmlFor="ann-title" className="profile-label">
            Title <span className="text-danger">*</span>
          </label>
          <input
            id="ann-title"
            className="form-control"
            maxLength={TITLE_MAX}
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>
        <div className="col-12">
          <label htmlFor="ann-body" className="profile-label">
            Message <span className="text-danger">*</span>
          </label>
          <textarea
            id="ann-body"
            className="form-control"
            rows={6}
            maxLength={BODY_MAX}
            value={form.body}
            onChange={(e) => set({ body: e.target.value })}
          ></textarea>
          <div className="fs-070 text-slate-500 text-end">
            {form.body.length.toLocaleString()} / {BODY_MAX.toLocaleString()}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-check">
            <input
              id="ann-important"
              type="checkbox"
              className="form-check-input"
              checked={form.important}
              onChange={(e) => set({ important: e.target.checked })}
            />
            <label htmlFor="ann-important" className="form-check-label fs-085">
              Important (shown in red)
            </label>
          </div>
          <div className="form-check">
            <input
              id="ann-pinned"
              type="checkbox"
              className="form-check-input"
              checked={form.pinned}
              onChange={(e) => set({ pinned: e.target.checked })}
            />
            <label htmlFor="ann-pinned" className="form-check-label fs-085">
              Pin to the top
            </label>
          </div>
        </div>
        <div className="col-md-3">
          <label htmlFor="ann-publish" className="profile-label">
            Publish on
          </label>
          <input
            id="ann-publish"
            type="datetime-local"
            className="form-control form-control-sm"
            value={form.publishAt}
            onChange={(e) => set({ publishAt: e.target.value })}
          />
          <div className="fs-070 text-slate-500">Empty = now</div>
        </div>
        <div className="col-md-3">
          <label htmlFor="ann-expires" className="profile-label">
            Show until
          </label>
          <input
            id="ann-expires"
            type="datetime-local"
            className="form-control form-control-sm"
            value={form.expiresAt}
            onChange={(e) => set({ expiresAt: e.target.value })}
          />
          <div className="fs-070 text-slate-500">Empty = no end</div>
        </div>
        {!branchOnly && (
          <div className="col-md-6">
            <label htmlFor="ann-company" className="profile-label">
              Company
            </label>
            <select
              id="ann-company"
              className="form-select"
              value={form.companyId}
              onChange={(e) =>
                set({ companyId: e.target.value, branchIds: [] })
              }
            >
              <option value="">
                {user.companyId ? "My company" : "Choose a company…"}
              </option>
              {superAdmin && (
                <option value={ALL_COMPANIES}>Every company</option>
              )}
              {companies
                .filter((c) => c.id !== user.companyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName || c.name}
                  </option>
                ))}
            </select>
            <div className="fs-070 text-slate-500 mt-1">
              Sub-companies see it too.
            </div>
          </div>
        )}
        {form.companyId !== ALL_COMPANIES && (
          <div className={branchOnly ? "col-12" : "col-md-6"}>
            <label htmlFor="ann-branches" className="profile-label">
              Only these branches{" "}
              {!branchOnly && (
                <span className="text-slate-400 fw-normal">(optional)</span>
              )}
            </label>
            <PickerChips
              id="ann-branches"
              options={options}
              value={form.branchIds}
              onChange={(ids) => set({ branchIds: ids })}
              placeholder="Add a branch…"
              emptyText={
                branchOnly ? "Pick at least one branch" : "The whole company"
              }
            />
            <div className="fs-070 text-slate-500 mt-1">
              Sub-branches see it too.
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

/** Administration → Announcements: post, schedule and retire dashboard announcements. */
const AnnouncementsPage = () => {
  const { noneManaged } = useBranchTargets("announcements");
  const { can } = usePermissions();
  const canModify = can("announcements", "modify");
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const query = useAnnouncements({ status, page, limit });
  const rows = query.data?.data || EMPTY;
  const remove = useDeleteAnnouncement();
  const toggle = useSaveAnnouncement();
  const [editing, setEditing] = useState(null); // { row? }

  const setActive = (row, isActive) =>
    toggle.mutate(
      { id: row.id, body: { isActive } },
      {
        onSuccess: () =>
          toast.success(
            isActive
              ? "Announcement is active again."
              : "Announcement turned off: it no longer shows on dashboards.",
          ),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not change it.")),
      },
    );

  const doDelete = (row) => {
    if (!window.confirm(`Remove "${row.title}" from the dashboards?`)) return;
    remove.mutate(row.id, {
      onSuccess: () => toast.success("Announcement removed."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not remove it.")),
    });
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <PageHeader
          back={{ to: "/admin", label: "Administration" }}
          title="Announcements"
          subtitle="Shown on everyone's dashboard (HR view and My view), pinned ones first."
          actions={
            canModify && (
              <span title={noneManaged ? BRANCH_SCOPE_HINT : undefined}>
                <button
                  type="button"
                  className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
                  onClick={() => setEditing({})}
                  disabled={noneManaged}
                >
                  <i className="bi bi-plus-lg me-2"></i>New announcement
                </button>
              </span>
            )
          }
        />

        {noneManaged && (
          <div className="alert alert-warning fs-085 py-2">
            <i className="bi bi-exclamation-triangle me-1"></i>
            {BRANCH_SCOPE_HINT}
          </div>
        )}
        <div className="approval-subtabs mb-3" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={status === t.key}
              className={`approval-subtab ${status === t.key ? "active" : ""}`}
              onClick={() => {
                setStatus(t.key);
                setPage(1);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {query.isError && (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(query.error, "Could not load announcements.")}
          </div>
        )}
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0 admin-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Announcement</th>
                  <th className="py-3">For</th>
                  <th className="py-3">Publish</th>
                  <th className="py-3">Until</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((r) => {
                  const st = STATUS[r.status] || {
                    label: r.status,
                    tone: "gray",
                  };
                  return (
                    <tr key={r.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-2">
                          {r.pinned && (
                            <i
                              className="bi bi-pin-angle-fill text-primary"
                              title="Pinned"
                            ></i>
                          )}
                          <span className="fw-semibold">{r.title}</span>
                          {r.priority === "IMPORTANT" && (
                            <span className="status-pill status-pill--red">
                              Important
                            </span>
                          )}
                        </div>
                        <div className="fs-075 text-slate-500 text-truncate mw-250px">
                          {r.body}
                        </div>
                      </td>
                      <td>{audienceText(r)}</td>
                      <td className="text-nowrap">
                        {formatDateTime(r.publishAt)}
                      </td>
                      <td className="text-nowrap">
                        {r.expiresAt ? formatDateTime(r.expiresAt) : "—"}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {canModify && (
                            <div className="form-check form-switch m-0">
                              <input
                                type="checkbox"
                                role="switch"
                                className="form-check-input"
                                checked={r.isActive !== false}
                                disabled={toggle.isPending}
                                onChange={(e) => setActive(r, e.target.checked)}
                                aria-label={`${r.title}: active`}
                                title={
                                  r.isActive !== false
                                    ? "Turn off: stops showing on dashboards"
                                    : "Turn back on"
                                }
                              />
                            </div>
                          )}
                          <span
                            className={`status-pill status-pill--${st.tone}`}
                          >
                            {st.label}
                          </span>
                        </div>
                      </td>
                      <td className="pe-4 text-end text-nowrap">
                        {canModify && (
                          <div className="d-inline-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-light border"
                              onClick={() => setEditing({ row: r })}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light border text-danger"
                              onClick={() => doDelete(r)}
                              aria-label={`Remove ${r.title}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!query.isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="approval-empty">
                      No {status === "all" ? "" : `${status} `}announcements.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            limit={limit}
            total={query.data?.total || 0}
            onPageChange={setPage}
            onLimitChange={(n) => {
              setLimit(n);
              setPage(1);
            }}
          />
        </div>
      </div>
      {editing && (
        <AnnouncementDialog
          row={editing.row}
          onClose={() => setEditing(null)}
        />
      )}
    </Layout>
  );
};

export default AnnouncementsPage;
